import { useEffect } from "react";

import { useAuth0 } from "@auth0/auth0-react";
import { datadogRum } from "@datadog/browser-rum";
import { useIdleTimer } from "react-idle-timer";
import { useShallow } from "zustand/react/shallow";

import { PageName } from "analytics/events";
import { useEmployeeStore } from "app/stores/useEmployeeStore";
import { useLayoutStore } from "app/stores/useLayoutStore";
import { config } from "config";
import { useEmployeeLogout } from "flows/Auth/hooks/useEmployeeLogout";
import { usePickingStore } from "flows/Picking/stores/usePickingStore";
import { useScanProgressStore } from "flows/Picking/stores/useScanProgressStore";
import { useVariablePriceStore } from "flows/Picking/stores/useVariablePriceStore";
import { useTimedActivityStore } from "shared/stores/useTimedActivityStore";
import { isNotNullNorUndefined } from "utils/tsHelpers";

const IDLE_LOGOUT_MS = config.environment.IDLE_LOGOUT_MINUTES * 60 * 1000;

export function useAuthStateEffects() {
  const { isAuthenticated, isLoading } = useAuth0();
  const isTimedActivityOngoing = useTimedActivityStore((state) =>
    isNotNullNorUndefined(state.timedActivityInstance),
  );

  const resetEmployeeStore = useEmployeeStore((state) => state.reset);

  const employeeLogout = useEmployeeLogout();

  const onIdle = () => employeeLogout("automatic");

  const { pageName } = useLayoutStore(
    useShallow((state) => ({
      pageName: state.pageName,
    })),
  );

  // ACTIONS
  const { activate, reset, pause } = useIdleTimer({
    onIdle,
    timeout: IDLE_LOGOUT_MS,
    startManually: true,
  });

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      // Make sure to reset employee store whenever the hub is logged out
      resetEmployeeStore();
      usePickingStore.getState().reset();
      useVariablePriceStore.getState().reset();
      useScanProgressStore.getState().reset();
      datadogRum.clearUser();
    }
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isAuthenticated]);

  useEffect(() => {
    if (isTimedActivityOngoing || pageName === PageName.PICKING_PAGE) {
      reset();
      pause();
    } else {
      activate();
    }
  }, [isTimedActivityOngoing, activate, reset, pause, pageName]);
}
